import { isHandleWithCrumb, makeAbsoluteUrl } from "@app/routing/utils";
import { FlexRow } from "@design-system/Layout/Flex";
import { Breadcrumb as BreadCrumbBase } from "@design-system/Navigation/Breadcrumb";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { useAtomValue } from "jotai";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatches } from "react-router-dom";
import { breadcrumbAtom } from "./ctx";

const TRANSLATION_NAMESPACE = "Breadcrumb";

const capitalizeFirstLetter = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

export const Breadcrumb = () => {
    const matches = useMatches();
    const { pathname: currentPathname } = useLocation();
    const breadcrumbState = useAtomValue(breadcrumbAtom);
    const { t } = useTranslation(TRANSLATION_NAMESPACE);

    const crumbs = useMemo(() => {
        return matches
            .filter(({ handle }) => isHandleWithCrumb(handle))
            .map(({ handle, pathname }) => {
                // necessary check for typescript
                if (!isHandleWithCrumb(handle)) {
                    throw new Error("Handle is not defined.");
                }

                const { crumb: Crumb, page } = handle;
                const name = breadcrumbState[page] ?? "";

                const pageName = capitalizeFirstLetter(name ?? t(page));

                const to = makeAbsoluteUrl(pathname);
                const preventScrollReset = shouldPreventScrollReset(
                    currentPathname,
                    pathname,
                );

                return {
                    Crumb,
                    pageName,
                    to,
                    preventScrollReset,
                };
            });
    }, [matches, t, currentPathname, breadcrumbState]);

    const hide = crumbs.length < 2;
    if (hide) return null;

    return (
        <FlexRow gap="5" alignItems="center">
            <BreadCrumbBase>
                <BreadCrumbBase.List>
                    {crumbs.map(({ Crumb, pageName, ...props }, idx) => (
                        <Fragment key={props.to}>
                            <BreadCrumbBase.Item>
                                <BreadCrumbBase.Link asChild>
                                    <Ellipsis
                                        asChild
                                        className="inline-block max-w-[250px]"
                                        hasTooltip
                                    >
                                        <Crumb {...props}>{pageName}</Crumb>
                                    </Ellipsis>
                                </BreadCrumbBase.Link>
                            </BreadCrumbBase.Item>
                            {idx < crumbs.length - 1 && (
                                <BreadCrumbBase.Separator />
                            )}
                        </Fragment>
                    ))}
                </BreadCrumbBase.List>
            </BreadCrumbBase>
        </FlexRow>
    );
};

function shouldPreventScrollReset(currentPathname: string, pathname: string) {
    if (currentPathname.endsWith("/"))
        currentPathname = currentPathname.slice(0, -1);
    if (pathname.endsWith("/")) pathname = pathname.slice(0, -1);

    return currentPathname === pathname;
}
